

import { useState, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Eye, Download, RefreshCw } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AuditLog, AuditLogFilters } from '@repo/ui/lib/api/types/audit-log.types';
import { useAuditLogs, useExportAuditLogs } from '@repo/ui/hooks/api/audit-logs';
import { DataTable } from '@repo/ui/components/ui/data-table';
import { Button } from '@repo/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@repo/ui/components/ui/avatar';
import { Badge } from '@repo/ui/components/ui/badge';
import {
  SeverityBadge,
  ActionBadge,
  CategoryBadge,
  StatusCodeBadge,
} from './components/audit-log-badges';

import { toast } from 'sonner';
import { AuditLogFiltersPanel } from './components/audit-log-filters';

interface AuditLogListProps {
  defaultFilters?: Partial<AuditLogFilters>;
  enableAutoRefresh?: boolean;
  refreshInterval?: number;
  onViewDetails?: (log: AuditLog) => void;
}

export function AuditLogList({
  defaultFilters = {},
  enableAutoRefresh = false,
  refreshInterval = 30000,
  onViewDetails,
}: AuditLogListProps) {
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    limit: 50,
    ...defaultFilters,
  });

  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, error, refetch } = useAuditLogs(filters, {
    refetchInterval: enableAutoRefresh ? refreshInterval : false,
  });

  const exportMutation = useExportAuditLogs({
    onSuccess: () => {
      toast.success('Export réussi', {
        description: 'Le fichier a été téléchargé avec succès',
      });
    },
    onError: (error) => {
      toast.error('Erreur d\'export', {
        description: error.message,
      });
    },
  });

  const columns = useMemo<ColumnDef<AuditLog>[]>(
    () => [
      {
        accessorKey: 'timestamp',
        header: 'Date/Heure',
        cell: ({ row }) => {
          const date = row.original.timestamp;
          return (
            <div className="flex flex-col">
              <span className="text-sm font-medium">
                {format(date, 'dd/MM/yyyy', { locale: fr })}
              </span>
              <span className="text-xs text-muted-foreground">
                {format(date, 'HH:mm:ss', { locale: fr })}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(date, { addSuffix: true, locale: fr })}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'user',
        header: 'Utilisateur',
        cell: ({ row }) => {
          const user = row.original.user;
          if (!user) {
            return <span className="text-muted-foreground text-sm">Système</span>;
          }
          const initials = `${user.prenom?.[0] || ''}${user.nom?.[0] || ''}`.toUpperCase();
          return (
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  {user.prenom} {user.nom}
                </span>
                <span className="text-xs text-muted-foreground">{user.email}</span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'action',
        header: 'Action',
        cell: ({ row }) => <ActionBadge action={row.original.action} showIcon />,
      },
      {
        accessorKey: 'resource',
        header: 'Ressource',
        cell: ({ row }) => {
          const { resource, resourceId } = row.original;
          return (
            <div className="flex flex-col">
              <span className="text-sm font-medium">{resource}</span>
              {resourceId && (
                <span className="text-xs text-muted-foreground font-mono">
                  ID: {resourceId}
                </span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'severity',
        header: 'Sévérité',
        cell: ({ row }) => <SeverityBadge severity={row.original.severity} showIcon />,
      },
      {
        accessorKey: 'category',
        header: 'Catégorie',
        cell: ({ row }) => <CategoryBadge category={row.original.category} />,
      },
      {
        accessorKey: 'statusCode',
        header: 'Statut',
        cell: ({ row }) => <StatusCodeBadge statusCode={row.original.statusCode} />,
      },
      {
        accessorKey: 'duration',
        header: 'Durée',
        cell: ({ row }) => {
          const duration = row.original.duration;
          if (!duration) return <span className="text-muted-foreground">-</span>;

          let color = 'text-green-600';
          if (duration > 1000) color = 'text-orange-600';
          if (duration > 3000) color = 'text-red-600';

          return (
            <span className={`text-sm font-mono ${color}`}>
              {duration}ms
            </span>
          );
        },
      },
      {
        accessorKey: 'ipAddress',
        header: 'IP',
        cell: ({ row }) => {
          const ip = row.original.ipAddress;
          if (!ip) return <span className="text-muted-foreground">-</span>;
          return <span className="text-xs font-mono">{ip}</span>;
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const log = row.original;
          return (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="sr-only">Ouvrir menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="z-50"
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails?.(log);
                  }}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Voir détails
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(log.id);
                    toast.success('ID copié dans le presse-papiers');
                  }}
                >
                  Copier ID
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [onViewDetails]
  );

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit: number) => {
    setFilters((prev) => ({ ...prev, limit, page: 1 }));
  };

  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search, page: 1 }));
  };

  const handleExport = (format: 'csv' | 'json') => {
    exportMutation.mutate({ filters, format });
  };

  const handleFiltersChange = (newFilters: Partial<AuditLogFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: filters.limit,
    });
  };

  if (error) {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
        <p className="text-sm text-destructive font-medium">
          Erreur lors du chargement des logs
        </p>
        <p className="text-sm text-destructive/80 mt-1">{error.message}</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-4"
          onClick={() => refetch()}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtres {showFilters ? '▼' : '▶'}
          </Button>
          {Object.keys(filters).length > 2 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
            >
              Réinitialiser
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                Exporter en CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('json')}>
                Exporter en JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <AuditLogFiltersPanel
          filters={filters}
          onChange={handleFiltersChange}
          onClear={handleClearFilters}
        />
      )}

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={data?.data || []}
        isLoading={isLoading}
        totalCount={data?.total || 0}
        pageCount={data?.totalPages || 1}
        currentPage={filters.page}
        pageSize={filters.limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        serverSidePagination
        searchKey="search"
        searchPlaceholder="Rechercher dans les logs..."
        onGlobalFilterChange={handleSearch}
        externalGlobalFilter={filters.search || ''}
      />
    </div>
  );
}
